import { useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import { cn } from '../lib/cn'
import { useAuth } from '../lib/auth'
import { useT } from '../lib/i18n'
import type { SimpleTKey } from '../lib/i18n'
import { logoFor, useBranding } from '../lib/branding'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { Icon } from '../components/ui/Icon'
import { Tooltip } from '../components/ui/Tooltip'
import { Popover, MenuItem } from '../components/ui/Popover'
import { toRailRows, visibleNavItems } from './nav'
import type { NavGroupDef, NavItem, NavMode, RailRow } from './nav'
import { AiCreditsNavRow } from './AiCreditsNavRow'

const MOBILE_SLOTS = 4

/** Whether the vertical rail shows a name next to each icon. Persisted, desktop only. */
const EXPANDED_STORAGE_KEY = 'pd.nav.expanded'

/** Simple by default for everyone — see `NavItem.simple` for where the line is drawn. */
const NAV_MODE_STORAGE_KEY = 'pd.nav.mode'

const railLink = (active: boolean, expanded: boolean): string =>
  cn(
    'ring-focus-inset relative flex h-12 w-full items-center transition-colors',
    expanded ? 'justify-start gap-3 px-4' : 'justify-center',
    active ? 'text-chrome-ink' : 'text-chrome-mute hover:text-chrome-ink',
  )

/** The label for the mode toggle names where it takes you, not the mode you are already in. */
const modeToggleKey = (mode: NavMode): SimpleTKey =>
  mode === 'simple' ? 'shell.nav.showAdvanced' : 'shell.nav.showSimple'

/** The brass left-edge bar marking the active screen. The rail's only accent. */
const ActiveBar = ({ orientation }: { orientation: 'left' | 'top' }): ReactElement => (
  <span
    aria-hidden="true"
    className={cn(
      'absolute bg-brass',
      orientation === 'left' ? 'top-2 bottom-2 left-0 w-0.75' : 'inset-x-4 top-0 h-0.75',
    )}
  />
)

/**
 * 64 px, icon-only, tooltip on hover — the vertical rail on md and up.
 * Under 768 px it becomes a fixed bottom bar: five slots plus an overflow sheet, because a
 * 64 px column would eat a fifth of a 360 px screen.
 */
export const NavRail = (): ReactElement => {
  const t = useT()
  const { isSuperadmin } = useAuth()
  const { branding } = useBranding()
  const navigate = useNavigate()
  const location = useLocation()
  const items = visibleNavItems(isSuperadmin)
  const logo = logoFor(branding, 'chrome')

  const overflowRef = useRef<HTMLButtonElement | null>(null)
  const [overflowOpen, setOverflowOpen] = useState(false)
  const [expanded, setExpanded] = useLocalStorage(EXPANDED_STORAGE_KEY, true)
  const [mode, setMode] = useLocalStorage<NavMode>(NAV_MODE_STORAGE_KEY, 'simple')

  /** Desktop-rail-only accordion state for a collapsible group, keyed by `group.key` — `undefined`
   *  means "follow the default" (open when one of its own links is the active route). */
  const [groupOverrides, setGroupOverrides] = useState<Record<string, boolean>>({})
  /** The one flyout popover a collapsed (icon-only) group opens — shared across every group
   *  rather than one `Popover` instance each, since at most one can ever be open at a time. */
  const flyoutAnchorRef = useRef<HTMLButtonElement | null>(null)
  const [flyoutGroup, setFlyoutGroup] = useState<{ key: string; label: string; items: NavItem[] } | null>(
    null,
  )

  /* A display filter, not a permission — every route below stays reachable by URL either way.
     The command palette (`CommandPalette.tsx`) deliberately ignores this: someone who has already
     opened it with Cmd+K is not the person Simple mode is for. */
  const modeItems = mode === 'advanced' ? items : items.filter((item) => item.simple)
  const primary = modeItems.filter((item) => item.primary).slice(0, MOBILE_SLOTS)
  const overflow = modeItems.filter((item) => !primary.includes(item))
  const toggleMode = (): void => {
    setMode((current) => (current === 'simple' ? 'advanced' : 'simple'))
  }

  const renderLink = (item: NavItem): ReactElement => {
    const label = t(item.labelKey)
    const link = (
      <NavLink
        to={item.to}
        // A stable hook for a guided tour to point at — see `features/tours`. Derived from the
        // route so every destination gets one for free, without a second list to keep in sync.
        data-tour={`nav-${item.to.split('/').pop() ?? ''}`}
        className={({ isActive }) => railLink(isActive, expanded)}
      >
        {({ isActive }) => (
          <>
            {isActive ? <ActiveBar orientation="left" /> : null}
            <Icon name={item.icon} size={20} className="shrink-0" />
            {expanded ? (
              <span className="t-small truncate">{label}</span>
            ) : (
              <span className="sr-only">{label}</span>
            )}
          </>
        )}
      </NavLink>
    )
    // The tooltip repeats the label the expanded rail already shows next to the icon.
    if (expanded) return <span key={item.to}>{link}</span>
    return (
      <Tooltip key={item.to} content={label} placement="right">
        {link}
      </Tooltip>
    )
  }

  const isGroupActive = (groupItems: readonly NavItem[]): boolean =>
    groupItems.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))

  /** One row of an open group, on the expanded rail — a smaller, indented `renderLink`. */
  const renderGroupChild = (item: NavItem): ReactElement => (
    <NavLink
      key={item.to}
      to={item.to}
      data-tour={`nav-${item.to.split('/').pop() ?? ''}`}
      className={({ isActive }) =>
        cn(
          'ring-focus-inset relative flex h-10 w-full items-center gap-2.5 pr-4 pl-10 transition-colors',
          isActive ? 'text-chrome-ink' : 'text-chrome-mute hover:text-chrome-ink',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? <ActiveBar orientation="left" /> : null}
          <Icon name={item.icon} size={16} className="shrink-0" />
          <span className="t-small truncate">{t(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  )

  /** "Post"/"Andamento"/"Pubblicità" — a collapsible header (expanded rail) or an icon that opens
   *  a flyout (collapsed rail). Desktop only; `toRailRows` never runs over the mobile lists. */
  const renderGroup = (key: string, group: NavGroupDef, groupItems: NavItem[]): ReactElement => {
    const label = t(group.labelKey)
    const active = isGroupActive(groupItems)

    if (!expanded) {
      return (
        <Tooltip key={key} content={label} placement="right">
          <button
            type="button"
            data-tour={`nav-group-${key}`}
            aria-haspopup="menu"
            aria-expanded={flyoutGroup?.key === key}
            onClick={(event) => {
              flyoutAnchorRef.current = event.currentTarget
              setFlyoutGroup((current) => (current?.key === key ? null : { key, label, items: groupItems }))
            }}
            className={railLink(active, false)}
          >
            <Icon name={group.icon} size={20} />
            <span className="sr-only">{label}</span>
          </button>
        </Tooltip>
      )
    }

    const open = groupOverrides[key] ?? active
    return (
      <div key={key}>
        <button
          type="button"
          data-tour={`nav-group-${key}`}
          aria-expanded={open}
          onClick={() => {
            setGroupOverrides((current) => ({ ...current, [key]: !open }))
          }}
          className={cn(
            'ring-focus-inset relative flex h-12 w-full items-center justify-between gap-3 px-4 transition-colors',
            active ? 'text-chrome-ink' : 'text-chrome-mute hover:text-chrome-ink',
          )}
        >
          <span className="flex min-w-0 items-center gap-3">
            {active ? <ActiveBar orientation="left" /> : null}
            <Icon name={group.icon} size={20} className="shrink-0" />
            <span className="t-small truncate">{label}</span>
          </span>
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} className="shrink-0" />
        </button>
        {open ? <div className="flex flex-col">{groupItems.map(renderGroupChild)}</div> : null}
      </div>
    )
  }

  const renderRow = (row: RailRow, index: number): ReactElement => {
    if (row.kind === 'item') return renderLink(row.item)
    if (row.kind === 'group') return renderGroup(row.key, row.group, row.items)
    return <div key={`divider-${index}`} aria-hidden="true" className="my-2 border-t border-chrome-600" />
  }

  return (
    <>
      {/* Vertical rail — md and up */}
      <nav
        aria-label={t('common.shell.mainNavigation')}
        className={cn(
          'hidden h-dvh shrink-0 flex-col border-r border-chrome-600 bg-chrome-900 transition-[width] duration-150 md:sticky md:top-0 md:flex',
          expanded ? 'w-56' : 'w-16',
        )}
      >
        <div
          className={cn(
            'flex h-14 items-center border-b border-chrome-600',
            expanded ? 'justify-start gap-2 px-4' : 'justify-center',
          )}
        >
          {logo ? (
            <img src={logo} alt={branding.productName} className="max-h-7 max-w-10 object-contain" />
          ) : (
            <span
              className="t-label shrink-0 text-brass"
              title={branding.productName}
              aria-label={expanded ? undefined : branding.productName}
            >
              {branding.productName.slice(0, 2).toUpperCase()}
            </span>
          )}
          {expanded ? (
            <span className="t-small truncate text-chrome-ink">{branding.productName}</span>
          ) : null}
        </div>
        <div className="flex flex-1 flex-col overflow-y-auto py-2">
          {toRailRows(modeItems).map(renderRow)}
          {expanded ? (
            <button
              type="button"
              onClick={toggleMode}
              className={cn(railLink(false, true), 'border-t border-chrome-600')}
            >
              <Icon name="more" size={20} className="shrink-0" />
              <span className="t-small truncate">{t(modeToggleKey(mode))}</span>
            </button>
          ) : (
            <Tooltip content={t(modeToggleKey(mode))} placement="right">
              <button
                type="button"
                onClick={toggleMode}
                className={cn(railLink(false, false), 'border-t border-chrome-600')}
              >
                <Icon name="more" size={20} />
                <span className="sr-only">{t(modeToggleKey(mode))}</span>
              </button>
            </Tooltip>
          )}
          <AiCreditsNavRow expanded={expanded} />
        </div>
        <div className="border-t border-chrome-600 py-2">
          {expanded ? (
            <button
              type="button"
              onClick={() => {
                setExpanded(false)
              }}
              className={railLink(false, true)}
            >
              <Icon name="chevron-left" size={20} className="shrink-0" />
              <span className="t-small truncate">{t('shell.nav.collapse')}</span>
            </button>
          ) : (
            <Tooltip content={t('shell.nav.expand')} placement="right">
              <button
                type="button"
                onClick={() => {
                  setExpanded(true)
                }}
                className={railLink(false, false)}
              >
                <Icon name="chevron-right" size={20} />
                <span className="sr-only">{t('shell.nav.expand')}</span>
              </button>
            </Tooltip>
          )}
        </div>
      </nav>

      {/* Bottom bar — under md */}
      <nav
        aria-label={t('common.shell.mainNavigation')}
        className="fixed inset-x-0 bottom-0 z-40 flex h-14 items-stretch border-t border-chrome-600 bg-chrome-900 pb-[env(safe-area-inset-bottom)] md:hidden"
      >
        {primary.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'ring-focus-inset relative flex flex-1 flex-col items-center justify-center gap-0.5 transition-colors',
                isActive ? 'text-chrome-ink' : 'text-chrome-mute',
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive ? <ActiveBar orientation="top" /> : null}
                <Icon name={item.icon} size={19} />
                <span className="t-label">{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
        {/* Always rendered, even with nothing else to overflow: the mode toggle lives at the
            bottom of this sheet on mobile too, and it must stay reachable in every mode. */}
        <button
          ref={overflowRef}
          type="button"
          onClick={() => {
            setOverflowOpen((previous) => !previous)
          }}
          aria-expanded={overflowOpen}
          aria-haspopup="menu"
          className="ring-focus-inset flex flex-1 flex-col items-center justify-center gap-0.5 text-chrome-mute"
        >
          <Icon name="more" size={19} />
          <span className="t-label">{t('shell.nav.more')}</span>
        </button>
      </nav>

      <Popover
        open={overflowOpen}
        onClose={() => {
          setOverflowOpen(false)
        }}
        anchorRef={overflowRef}
        placement="top"
        align="end"
        surface="chrome"
        role="menu"
        label={t('shell.nav.moreSections')}
        className="py-1"
      >
        {overflow.map((item) => (
          <MenuItem
            key={item.to}
            surface="chrome"
            icon={<Icon name={item.icon} size={16} />}
            onSelect={() => {
              setOverflowOpen(false)
              void navigate(item.to)
            }}
          >
            {t(item.labelKey)}
          </MenuItem>
        ))}
        <MenuItem
          surface="chrome"
          icon={<Icon name="more" size={16} />}
          onSelect={() => {
            setOverflowOpen(false)
            toggleMode()
          }}
        >
          {t(modeToggleKey(mode))}
        </MenuItem>
      </Popover>

      {/* The collapsed desktop rail's one group flyout — shared across all three groups, since
          only one can ever be open. */}
      <Popover
        open={flyoutGroup !== null}
        onClose={() => {
          setFlyoutGroup(null)
        }}
        anchorRef={flyoutAnchorRef}
        placement="right"
        surface="chrome"
        role="menu"
        label={flyoutGroup?.label ?? ''}
        className="py-1"
      >
        {(flyoutGroup?.items ?? []).map((item) => (
          <MenuItem
            key={item.to}
            surface="chrome"
            icon={<Icon name={item.icon} size={16} />}
            onSelect={() => {
              setFlyoutGroup(null)
              void navigate(item.to)
            }}
          >
            {t(item.labelKey)}
          </MenuItem>
        ))}
      </Popover>
    </>
  )
}
